<template>
  <div class="my-rtp">
    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p>Loading your RTP program...</p>
    </div>

    <!-- No Active RTP -->
    <div v-else-if="!hasActiveRTP" class="no-rtp-state">
      <div class="no-rtp-content">
        <i class="bi bi-check-circle"></i>
        <h4>No Active RTP Program</h4>
        <p>You don't currently have an active Return-to-Play program.</p>
        <p class="text-muted">If you've been injured and need an RTP program, please speak with your physio.</p>
      </div>
    </div>

    <!-- Active RTP -->
    <div v-else class="rtp-active">
      <!-- RTP Header -->
      <div class="rtp-header">
        <div class="injury-info">
          <div class="injury-badge">
            <i class="bi bi-bandaid"></i>
          </div>
          <div class="injury-details">
            <h4>{{ rtpData.rtp.injury_type }}</h4>
            <span class="injury-date">
              <i class="bi bi-calendar3"></i>
              Since {{ formatDate(rtpData.rtp.injury_date) }}
            </span>
          </div>
        </div>

        <div class="phase-info">
          <div class="phase-badge" :style="{ backgroundColor: getPhaseColor(rtpData.rtp.rtp_phase) }">
            Phase {{ rtpData.rtp.rtp_phase }}
          </div>
          <span class="phase-name">{{ getPhaseName(rtpData.rtp.rtp_phase) }}</span>
          <span class="days-in-phase">{{ rtpData.rtp.days_in_phase || 0 }} days</span>
        </div>
      </div>

      <!-- Today's Progress -->
      <div class="progress-section">
        <div class="progress-header">
          <h5>
            <i class="bi bi-calendar-check"></i>
            Today's Exercises
          </h5>
          <div class="progress-stats">
            <span class="completed-count">
              {{ completedToday }} / {{ totalExercises }}
            </span>
            <span class="compliance-badge" :class="complianceClass">
              {{ complianceToday }}%
            </span>
          </div>
        </div>

        <div class="progress-bar-container">
          <div class="progress">
            <div
              class="progress-bar"
              :class="complianceClass"
              role="progressbar"
              :style="{ width: complianceToday + '%' }"
              :aria-valuenow="complianceToday"
              aria-valuemin="0"
              aria-valuemax="100"
            ></div>
          </div>
        </div>
      </div>

      <!-- Exercise List -->
      <div class="exercises-section">
        <div v-if="exercises.length === 0" class="no-exercises">
          <i class="bi bi-clipboard-check"></i>
          <p>No exercises assigned yet.</p>
          <p class="text-muted">Your physio will assign exercises to your program soon.</p>
        </div>

        <div v-else class="exercise-list">
          <div
            v-for="exercise in exercises"
            :key="exercise.assignment_id"
            class="exercise-item"
            :class="{ completed: exercise.completion_id }"
          >
            <div class="exercise-main">
              <button
                class="complete-btn"
                :class="{ checked: exercise.completion_id }"
                @click="toggleExerciseComplete(exercise)"
                :disabled="togglingExercise === exercise.assignment_id"
              >
                <span v-if="togglingExercise === exercise.assignment_id" class="spinner-border spinner-border-sm"></span>
                <i v-else :class="exercise.completion_id ? 'bi bi-check-circle-fill' : 'bi bi-circle'"></i>
              </button>

              <div class="exercise-info" @click="expandedExercise = expandedExercise === exercise.assignment_id ? null : exercise.assignment_id">
                <span class="exercise-name">{{ exercise.exercise_name }}</span>
                <div class="exercise-meta">
                  <span v-if="exercise.sets_reps" class="meta-item">
                    <i class="bi bi-repeat"></i> {{ exercise.sets_reps }}
                  </span>
                  <span v-if="exercise.duration" class="meta-item">
                    <i class="bi bi-clock"></i> {{ exercise.duration }}
                  </span>
                  <span class="meta-item category-badge">{{ formatCategory(exercise.category) }}</span>
                </div>
              </div>

              <button class="expand-btn" @click="expandedExercise = expandedExercise === exercise.assignment_id ? null : exercise.assignment_id">
                <i :class="expandedExercise === exercise.assignment_id ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
              </button>
            </div>

            <!-- Expanded Details -->
            <transition name="slide">
              <div v-if="expandedExercise === exercise.assignment_id" class="exercise-details">
                <p v-if="exercise.description" class="exercise-desc">{{ exercise.description }}</p>

                <div v-if="exercise.video_url" class="video-link">
                  <a :href="exercise.video_url" target="_blank" class="btn btn-outline-primary btn-sm">
                    <i class="bi bi-play-circle"></i> Watch Demo
                  </a>
                </div>

                <div v-if="exercise.precautions" class="precautions">
                  <i class="bi bi-exclamation-triangle"></i>
                  {{ exercise.precautions }}
                </div>

                <!-- Completion Time -->
                <div v-if="exercise.completion_id" class="completion-info">
                  <i class="bi bi-check-circle-fill"></i>
                  Completed at {{ formatTime(exercise.completed_time) }}
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>

      <!-- Compliance History -->
      <div v-if="showComplianceHistory" class="compliance-section">
        <h5>
          <i class="bi bi-graph-up"></i>
          7-Day Compliance
        </h5>
        <div class="compliance-chart">
          <!-- Simple visual representation of last 7 days -->
          <div class="day-indicators">
            <div
              v-for="(day, index) in last7Days"
              :key="index"
              class="day-indicator"
              :class="getDayComplianceClass(day)"
              :title="day.date + ': ' + day.percentage + '%'"
            >
              <span class="day-label">{{ day.label }}</span>
              <div class="day-bar" :style="{ height: day.percentage + '%' }"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { ReturnToPlayService, RTP_PHASES } from "@/services/rtp-service.js";

export default {
  name: "MyRTP",
  props: {
    playerId: {
      type: [Number, String],
      default: null
    },
    showComplianceHistory: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      loading: true,
      rtpData: null,
      exercises: [],
      expandedExercise: null,
      togglingExercise: null,
      last7Days: []
    };
  },
  computed: {
    ...mapGetters(["getProfile"]),
    effectivePlayerId() {
      return this.playerId || this.getProfile?.id;
    },
    hasActiveRTP() {
      return this.rtpData?.has_active_rtp;
    },
    totalExercises() {
      return this.exercises.length;
    },
    completedToday() {
      return this.exercises.filter(e => e.completion_id).length;
    },
    complianceToday() {
      if (this.totalExercises === 0) return 0;
      return Math.round((this.completedToday / this.totalExercises) * 100);
    },
    complianceClass() {
      if (this.complianceToday >= 80) return 'bg-success';
      if (this.complianceToday >= 50) return 'bg-warning';
      return 'bg-danger';
    }
  },
  watch: {
    effectivePlayerId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadPlayerRTP();
        }
      }
    }
  },
  methods: {
    async loadPlayerRTP() {
      if (!this.effectivePlayerId) {
        this.loading = false;
        return;
      }

      this.loading = true;
      try {
        const response = await ReturnToPlayService.getPlayerActiveRTP(this.effectivePlayerId);
        if (response.data.success) {
          this.rtpData = response.data;
          this.exercises = response.data.exercises || [];
        }
      } catch (error) {
        console.error('Error loading player RTP:', error);
      } finally {
        this.loading = false;
      }
    },

    async toggleExerciseComplete(exercise) {
      if (this.togglingExercise) return;

      this.togglingExercise = exercise.assignment_id;
      try {
        if (exercise.completion_id) {
          // Undo completion
          const response = await ReturnToPlayService.undoExerciseCompletion(exercise.completion_id);
          if (response.data.success) {
            exercise.completion_id = null;
            exercise.completed_time = null;
          }
        } else {
          // Mark as complete
          const response = await ReturnToPlayService.completeExercise({
            assignment_id: exercise.assignment_id,
            player_id: this.effectivePlayerId,
            injury_id: this.rtpData.rtp.injury_id,
            completed_by: this.effectivePlayerId
          });
          if (response.data.success) {
            exercise.completion_id = response.data.completion_id;
            exercise.completed_time = new Date().toTimeString().slice(0, 8);
          }
        }
      } catch (error) {
        console.error('Error toggling exercise:', error);
        this.$toast?.error('Error updating exercise');
      } finally {
        this.togglingExercise = null;
      }
    },

    getPhaseColor(phase) {
      const colors = {
        1: '#dc3545',
        2: '#fd7e14',
        3: '#ffc107',
        4: '#20c997',
        5: '#28a745'
      };
      return colors[phase] || '#6c757d';
    },

    getPhaseName(phase) {
      return RTP_PHASES[phase]?.name || 'Unknown';
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    },

    formatTime(timeStr) {
      if (!timeStr) return '';
      // Convert 24h to 12h format
      const [hours, minutes] = timeStr.split(':');
      const h = parseInt(hours);
      const ampm = h >= 12 ? 'pm' : 'am';
      const h12 = h % 12 || 12;
      return `${h12}:${minutes}${ampm}`;
    },

    formatCategory(category) {
      if (!category) return '';
      return category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    },

    getDayComplianceClass(day) {
      if (day.percentage >= 80) return 'good';
      if (day.percentage >= 50) return 'partial';
      if (day.percentage > 0) return 'low';
      return 'none';
    }
  }
};
</script>

<style scoped lang="scss">
.my-rtp {
  padding: 0;
}

.loading-state,
.no-rtp-state {
  text-align: center;
  padding: 40px 20px;
}

.loading-state .spinner-border {
  width: 2.5rem;
  height: 2.5rem;
  margin-bottom: 16px;
}

.no-rtp-content {
  i {
    font-size: 64px;
    color: #28a745;
    margin-bottom: 16px;
  }

  h4 {
    margin-bottom: 8px;
    color: #495057;
  }

  p {
    margin: 0 0 8px 0;
    color: #6c757d;
  }
}

.rtp-active {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* RTP Header */
.rtp-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 12px;
  flex-wrap: wrap;
  gap: 16px;
}

.injury-info {
  display: flex;
  align-items: center;
  gap: 14px;
}

.injury-badge {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #ffebee;
  color: #c62828;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.injury-details {
  h4 {
    margin: 0 0 4px 0;
    font-size: 16px;
    font-weight: 600;
  }

  .injury-date {
    font-size: 13px;
    color: #6c757d;
    display: flex;
    align-items: center;
    gap: 6px;

    i {
      font-size: 12px;
    }
  }
}

.phase-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.phase-badge {
  padding: 6px 14px;
  border-radius: 20px;
  color: white;
  font-weight: 600;
  font-size: 13px;
}

.phase-name {
  font-size: 12px;
  color: #6c757d;
}

.days-in-phase {
  font-size: 11px;
  color: #9e9e9e;
}

/* Progress Section */
.progress-section {
  padding: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  h5 {
    margin: 0;
    font-size: 15px;
    display: flex;
    align-items: center;
    gap: 8px;

    i {
      color: #007bff;
    }
  }
}

.progress-stats {
  display: flex;
  align-items: center;
  gap: 10px;
}

.completed-count {
  font-size: 14px;
  font-weight: 600;
  color: #495057;
}

.compliance-badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  color: white;
}

.progress-bar-container .progress {
  height: 10px;
  border-radius: 5px;
  background: #e9ecef;
}

.progress-bar {
  border-radius: 5px;
  transition: width 0.3s ease;
}

/* Exercises Section */
.exercises-section {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.no-exercises {
  padding: 40px 20px;
  text-align: center;

  i {
    font-size: 48px;
    color: #dee2e6;
    margin-bottom: 16px;
  }

  p {
    margin: 0 0 8px 0;
    color: #6c757d;
  }
}

.exercise-list {
  padding: 0;
}

.exercise-item {
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.2s;

  &:last-child {
    border-bottom: none;
  }

  &.completed {
    background: #f0fff4;

    .exercise-name {
      color: #28a745;
    }
  }
}

.exercise-main {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  gap: 14px;
}

.complete-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: #f5f5f5;
  color: #9e9e9e;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 22px;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: #e8f5e9;
    color: #28a745;
  }

  &.checked {
    background: #28a745;
    color: white;

    &:hover:not(:disabled) {
      background: #1e7e34;
    }
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.exercise-info {
  flex: 1;
  min-width: 0;
  cursor: pointer;
}

.exercise-name {
  display: block;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px;
  color: #333;
}

.exercise-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.meta-item {
  font-size: 12px;
  color: #6c757d;
  display: flex;
  align-items: center;
  gap: 4px;

  i {
    font-size: 11px;
  }
}

.category-badge {
  padding: 2px 8px;
  background: #e9ecef;
  border-radius: 10px;
  font-size: 11px;
}

.expand-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #9e9e9e;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  flex-shrink: 0;

  &:hover {
    color: #333;
  }
}

.exercise-details {
  padding: 0 16px 16px 70px;
}

.exercise-desc {
  font-size: 13px;
  color: #555;
  line-height: 1.5;
  margin-bottom: 12px;
}

.video-link {
  margin-bottom: 12px;

  .btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
}

.precautions {
  font-size: 12px;
  padding: 10px 12px;
  background: #fff8e1;
  border-radius: 8px;
  color: #856404;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;

  i {
    color: #ffc107;
    flex-shrink: 0;
    margin-top: 2px;
  }
}

.completion-info {
  font-size: 12px;
  color: #28a745;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Compliance Section */
.compliance-section {
  padding: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  h5 {
    margin: 0 0 16px 0;
    font-size: 15px;
    display: flex;
    align-items: center;
    gap: 8px;

    i {
      color: #007bff;
    }
  }
}

.day-indicators {
  display: flex;
  justify-content: space-between;
  height: 80px;
  gap: 8px;
}

.day-indicator {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;

  .day-label {
    font-size: 10px;
    color: #9e9e9e;
    text-transform: uppercase;
  }

  .day-bar {
    width: 100%;
    border-radius: 4px 4px 0 0;
    transition: height 0.3s ease;
    min-height: 4px;
  }

  &.good .day-bar {
    background: #28a745;
  }

  &.partial .day-bar {
    background: #ffc107;
  }

  &.low .day-bar {
    background: #dc3545;
  }

  &.none .day-bar {
    background: #e9ecef;
  }
}

/* Transitions */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
  max-height: 300px;
}

.slide-enter-from,
.slide-leave-to {
  opacity: 0;
  max-height: 0;
  overflow: hidden;
}

/* Responsive */
@media (max-width: 576px) {
  .rtp-header {
    flex-direction: column;
    align-items: stretch;
  }

  .phase-info {
    align-items: flex-start;
    flex-direction: row;
    gap: 10px;
  }

  .exercise-details {
    padding-left: 16px;
  }
}
</style>
